"use client";

import { useRef, useState } from "react";
import { devLabel, devLabelPanel } from "@/lib/dev-labels";
import styles from "./BatchPanel.module.css";

/**
 * BatchPanel — Phase 25-AC Group 1 #1 (Task 18 missing #1).
 *
 * Mockup source: docs/mockups/extras.html lines 203-220 (card 61).
 *
 * Compact panel variant of BatchMode. Lives below the ToolsCardGrid on the
 * Welcome page as a quick-add multi-URL batch queue. Each row has a
 * status pill (대기 / 분석 N% / 완료 N / 실패) + remove button. Max 20
 * items in queue; "전체 시작" kicks the batch job via onStartAll.
 *
 * Source-of-truth for the full Welcome batch mode is `BatchMode.tsx` —
 * this is a slimmer variant for the tools grid context.
 *
 * Phase 25-AC Group 1.
 */

const MAX_ITEMS = 20;
const COST_PER_ITEM = 2;

export type BatchPanelStatus = "queued" | "running" | "done" | "error";

export interface BatchPanelItem {
	id: number;
	url: string;
	status: BatchPanelStatus;
	progress?: number;
	doneCount?: number;
}

const INITIAL: BatchPanelItem[] = [
	{ id: 1, url: "youtube.com/watch?v=oVqhJ03qoUY", status: "done", doneCount: 7 },
	{ id: 2, url: "youtube.com/watch?v=abc123xyz", status: "running", progress: 62 },
	{ id: 3, url: "youtube.com/watch?v=def456...", status: "running", progress: 31 },
	{ id: 4, url: "youtube.com/watch?v=ghi789...", status: "queued" },
	{ id: 5, url: "youtube.com/watch?v=jkl012...", status: "error" },
];

export interface BatchPanelProps {
	initialItems?: BatchPanelItem[];
	onStartAll?: (items: BatchPanelItem[]) => void;
}

export function BatchPanel({
	initialItems = INITIAL,
	onStartAll,
}: BatchPanelProps) {
	const [items, setItems] = useState<BatchPanelItem[]>(initialItems);
	const [urlInput, setUrlInput] = useState("");
	const [paused, setPaused] = useState(false);
	const seqRef = useRef(initialItems.length + 1);

	function nextId() {
		const id = seqRef.current;
		seqRef.current += 1;
		return id;
	}

	function addUrl() {
		const v = urlInput.trim();
		if (!v) return;
		if (items.length >= MAX_ITEMS) return;
		setItems((prev) => [
			...prev,
			{ id: nextId(), url: v, status: "queued" },
		]);
		setUrlInput("");
	}

	function removeItem(id: number) {
		setItems((prev) => prev.filter((i) => i.id !== id));
	}

	function startAll() {
		setPaused(false);
		onStartAll?.(items);
	}

	const runningCount = items.filter((i) => i.status === "running").length;
	const totalCost = items.length * COST_PER_ITEM;
	const canAdd = items.length < MAX_ITEMS;

	return (
		<section
			className={styles.panel}
			data-testid="batch-panel"
			{...devLabelPanel("61")}
		>
			<div className={styles.head}>
				<span className={styles.headBadge}>61</span>
				멀티 batch — {items.length} / {MAX_ITEMS}
			</div>
			<div className={styles.subText}>
				10+ URL 한 번에 입력 → 큐 처리 → 각각 클립 N개씩 자동 생성. 진행률 / 상태 별도 표시.
			</div>

			<div className={styles.toolbar} {...devLabel("61-A")}>
				<button
					type="button"
					className={`${styles.btn} ${styles.btnPrimary}`}
					onClick={addUrl}
					disabled={!urlInput.trim() || !canAdd}
					data-testid="batch-panel-add"
				>
					<BatchIcon />+ URL 추가
				</button>
				<button
					type="button"
					className={`${styles.btn} ${styles.btnOutline}`}
					onClick={startAll}
					disabled={items.length === 0}
					data-testid="batch-panel-start"
					{...devLabel("61-B")}
				>
					전체 시작
				</button>
				<button
					type="button"
					className={`${styles.btn} ${styles.btnOutline}`}
					onClick={() => setPaused((p) => !p)}
					data-testid="batch-panel-pause"
					{...devLabel("61-C")}
				>
					{paused ? "재개" : "일시정지"}
				</button>
			</div>

			<div
				style={{ display: "flex", gap: 6, alignItems: "center" }}
				{...devLabel("61-input")}
			>
				<input
					type="url"
					value={urlInput}
					onChange={(e) => setUrlInput(e.target.value)}
					onKeyDown={(e) => {
						if (e.key === "Enter") {
							e.preventDefault();
							addUrl();
						}
					}}
					placeholder="URL 입력 후 Enter (youtube / tiktok / vimeo)"
					className={styles.urlInput}
					aria-label="배치 큐 URL 입력"
					data-testid="batch-panel-url-input"
					disabled={!canAdd}
				/>
			</div>

			<div className={styles.queue} data-testid="batch-panel-queue">
				{items.length === 0 ? (
					<div className={styles.empty}>
						큐가 비어 있습니다. URL을 추가하세요.
					</div>
				) : (
					items.map((item) => (
						<BatchPanelRow
							key={item.id}
							item={item}
							onRemove={() => removeItem(item.id)}
						/>
					))
				)}
			</div>

			<div className={styles.summary} {...devLabel("61-summary")}>
				<div>
					분석 중{" "}
					<span className={styles.summaryCost}>{runningCount}</span> · 큐{" "}
					<span className={styles.summaryCost}>{items.length}</span> 항목
				</div>
				<div>
					예상 비용{" "}
					<span className={styles.summaryCost}>{totalCost} C</span>
				</div>
			</div>
		</section>
	);
}

function BatchPanelRow({
	item,
	onRemove,
}: {
	item: BatchPanelItem;
	onRemove: () => void;
}) {
	return (
		<div className={styles.row} {...devLabel("61-D")}>
			<span className={styles.url}>{item.url}</span>
			<StatusPill status={item.status} progress={item.progress} doneCount={item.doneCount} />
			<button
				type="button"
				className={styles.remove}
				onClick={onRemove}
				aria-label={`${item.url} 제거`}
				data-testid="batch-panel-remove"
			>
				×
			</button>
		</div>
	);
}

function StatusPill({
	status,
	progress,
	doneCount,
}: {
	status: BatchPanelStatus;
	progress?: number;
	doneCount?: number;
}) {
	switch (status) {
		case "done":
			return (
				<span className={`${styles.stat} ${styles.statD}`}>
					완료 {doneCount ?? 0}
				</span>
			);
		case "running":
			return (
				<span className={`${styles.stat} ${styles.statR}`}>
					분석 {progress ?? 0}%
				</span>
			);
		case "error":
			return <span className={`${styles.stat} ${styles.statE}`}>실패</span>;
		case "queued":
		default:
			return <span className={`${styles.stat} ${styles.statQ}`}>대기</span>;
	}
}

function BatchIcon() {
	return (
		<svg
			width="10"
			height="10"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			aria-hidden
		>
			<rect x="3" y="3" width="7" height="7" />
			<rect x="14" y="3" width="7" height="7" />
			<rect x="14" y="14" width="7" height="7" />
			<rect x="3" y="14" width="7" height="7" />
		</svg>
	);
}
